import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { patientApiService } from '../../services/api';
import { Printer, AlertCircle, FileText, Loader2 } from 'lucide-react';
import { cptLinesHaveMinutes, cptUnits, formatCptMinutes } from '@prism-ehr/shared/utils/cptCodes';
import { resolveInvoiceProvider } from '@prism-ehr/shared/utils/superbillProvider';

const POS_MAP: Record<string, string> = { office: '11', telehealth: '02', home: '12', facility: '21' };

interface SuperbillData {
  invoice_number: string;
  invoice_date: string | number;
  status: string;
  line_items: any[];
  visit_notes: Record<string, any>;
  subtotal: number;
  total_amount: number;
  amount_paid: number;
  balance_due: number;
  insurance_claim_id?: string;
  insurance_portion?: number;
  secondary_insurance_portion?: number;
  patient_responsibility?: number;
  tax_amount?: number;
  discount_amount?: number;
  organization: {
    name: string;
    phone?: string;
    tax_id?: string;
    address?: { line1?: string; line2?: string; city?: string; state?: string; zip?: string };
    settings?: { branding?: { primary_color?: string; logo_url?: string } };
  };
  patient: {
    first_name: string;
    last_name: string;
    birthday?: string;
    phone?: string;
    address?: { line1?: string; line2?: string; city?: string; state?: string; zip?: string };
    insurance?: { payer_name?: string; member_id?: string; group_number?: string; is_primary?: boolean }[];
  };
  recipient?: any;
}

function formatDate(val: string | number | undefined): string {
  if (!val) return '';
  const d = new Date(typeof val === 'number' && val < 1e12 ? val * 1000 : val);
  return d.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
}

function formatCurrency(amount: number | undefined): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
}

export function SuperbillView() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [data, setData] = useState<SuperbillData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('No document token provided.');
      setLoading(false);
      return;
    }
    loadSuperbill();
  }, [token]);

  const loadSuperbill = async () => {
    try {
      const result = await patientApiService.publicRequest(
        `/public/document-superbill?token=${encodeURIComponent(token!)}`,
        { method: 'GET' }
      );
      setData(result);
    } catch (err: any) {
      if (err.status === 403) {
        setError('This document link has expired. Please contact your provider for a new link.');
      } else if (err.status === 404) {
        setError('Document not found. The link may be invalid or expired.');
      } else {
        setError('Unable to load document. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-gray-600">Loading document...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md text-center p-8">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Document</h1>
          <p className="text-gray-600">{error || 'An unexpected error occurred.'}</p>
        </div>
      </div>
    );
  }

  const org = data.organization;
  const patient = data.patient;
  const visitNotes = data.visit_notes || {};
  const provider = resolveInvoiceProvider(data);
  const lineItems = data.line_items || [];
  const showMin = cptLinesHaveMinutes(lineItems.flatMap((item: any) => item.metadata?.cpt_codes || []));
  const branding = org.settings?.branding || {};
  const primaryColor = branding.primary_color || '#0fadc7';

  const resolveLogoUrl = (path?: string) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    try { return new URL(import.meta.env.VITE_API_BASE_URL || '').origin + path; }
    catch { return path; }
  };
  const logoUrl = resolveLogoUrl(branding.logo_url);

  const diagnosisMap = new Map<string, { code: string; description?: string; is_primary?: boolean }>();
  lineItems.forEach((item: any) => {
    (item.metadata?.icd10_codes || []).forEach((dx: any) => {
      if (!diagnosisMap.has(dx.code)) {
        diagnosisMap.set(dx.code, { ...dx });
      } else if (dx.is_primary) {
        diagnosisMap.get(dx.code)!.is_primary = true;
      }
    });
  });
  const diagnoses = Array.from(diagnosisMap.values()).sort((a, b) => {
    if (a.is_primary && !b.is_primary) return -1;
    if (!a.is_primary && b.is_primary) return 1;
    return 0;
  });

  return (
    <div className="min-h-screen bg-gray-100 print:bg-white">
      {/* Print button bar — hidden in print */}
      <div className="print:hidden bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-gray-600">
            <FileText className="h-5 w-5" />
            <span className="font-medium">Superbill / Invoice</span>
          </div>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 px-4 py-2 text-white rounded-md text-sm font-medium"
            style={{ backgroundColor: primaryColor }}
          >
            <Printer className="h-4 w-4" />
            Print Superbill
          </button>
        </div>
      </div>

      {/* Document */}
      <div className="max-w-4xl mx-auto p-6 print:p-0 print:max-w-none">
        <div className="bg-white rounded-lg shadow print:shadow-none p-8 print:p-6">

          {/* Header */}
          <div className="border-b-2 pb-4 mb-6" style={{ borderColor: primaryColor }}>
            {logoUrl && (
              <img src={logoUrl} alt={org.name} className="h-10 w-auto mb-2 print:h-8" />
            )}
            <h1 className="text-2xl font-bold" style={{ color: primaryColor }}>INVOICE / SUPERBILL</h1>
            <div className="flex justify-between mt-3">
              <div className="text-sm text-gray-600">
                <p className="font-medium text-gray-900">{org.name}</p>
                {org.address && (
                  <p>{org.address.line1}{org.address.line2 ? `, ${org.address.line2}` : ''}, {org.address.city} {org.address.state} {org.address.zip}</p>
                )}
                {org.phone && <p>{org.phone}</p>}
              </div>
              <div className="text-right text-sm">
                <p className="text-gray-500">Invoice Number</p>
                <p className="font-bold text-lg">#{data.invoice_number}</p>
                <p className="text-gray-500 mt-1">Date</p>
                <p className="font-medium">{formatDate(data.invoice_date)}</p>
              </div>
            </div>
          </div>

          {/* Provider & Patient Info */}
          <div className="grid grid-cols-2 gap-8 mb-6 border-b pb-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 bg-gray-100 px-3 py-1.5 mb-3">Provider Information</h3>
              <div className="space-y-1 text-sm">
                {provider?.name && (
                  <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">Provider:</span><span>{provider.name}{provider.credentials ? `, ${provider.credentials}` : ''}</span></div>
                )}
                {provider?.designation && (
                  <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">Designation:</span><span>{provider.designation}</span></div>
                )}
                <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">Practice:</span><span>{org.name}</span></div>
                {org.address && (
                  <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">Address:</span><span>{org.address.line1}{org.address.line2 ? `, ${org.address.line2}` : ''}, {org.address.city} {org.address.state} {org.address.zip}</span></div>
                )}
                {org.phone && (
                  <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">Phone:</span><span>{org.phone}</span></div>
                )}
                {provider?.npi && (
                  <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">NPI:</span><span>{provider.npi}</span></div>
                )}
                {org.tax_id && (
                  <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">Tax ID (EIN):</span><span>{org.tax_id}</span></div>
                )}
                {provider?.license_number && (
                  <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">License #:</span><span>{provider.license_number}</span></div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 bg-gray-100 px-3 py-1.5 mb-3">Patient Information</h3>
              <div className="space-y-1 text-sm">
                <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">Patient:</span><span>{patient.first_name} {patient.last_name}</span></div>
                {patient.birthday && (
                  <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">DOB:</span><span>{formatDate(patient.birthday)}</span></div>
                )}
                {patient.address && (
                  <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">Address:</span><span>{patient.address.line1}{patient.address.line2 ? `, ${patient.address.line2}` : ''}, {patient.address.city} {patient.address.state} {patient.address.zip}</span></div>
                )}
                {patient.phone && (
                  <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">Phone:</span><span>{patient.phone}</span></div>
                )}
                {patient.insurance?.[0] && (
                  <>
                    <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">Insurance:</span><span>{patient.insurance[0].payer_name}</span></div>
                    <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">Member ID:</span><span>{patient.insurance[0].member_id}</span></div>
                    {patient.insurance[0].group_number && (
                      <div className="flex"><span className="w-28 text-gray-500 font-medium shrink-0">Group #:</span><span>{patient.insurance[0].group_number}</span></div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ICD-10 Diagnosis Table */}
          {diagnoses.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-900 bg-gray-100 px-3 py-1.5 mb-2">Diagnosis Codes (ICD-10)</h3>
              <table className="w-full">
                <thead className="border-b">
                  <tr>
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 w-[15%]">Code</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-600 w-[70%]">Description</th>
                    <th className="text-center py-2 px-3 text-xs font-medium text-gray-600 w-[15%]">Primary</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {diagnoses.map((dx, idx) => (
                    <tr key={dx.code} className={idx % 2 === 1 ? 'bg-gray-50' : ''}>
                      <td className="py-2 px-3 text-sm font-mono">{dx.code}</td>
                      <td className="py-2 px-3 text-sm">{dx.description}</td>
                      <td className="py-2 px-3 text-sm text-center">{dx.is_primary ? '✓' : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* CPT Services Table */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 bg-gray-100 px-3 py-1.5 mb-2">Services Rendered (CPT Codes)</h3>
            <table className="w-full">
              <thead className="border-b">
                <tr>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-600">Date</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-600">POS</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-600">CPT Code</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-600">Description</th>
                  <th className="text-left py-2 px-3 text-xs font-medium text-gray-600">Modifiers</th>
                  <th className="text-right py-2 px-3 text-xs font-medium text-gray-600">Units</th>
                  {showMin && <th className="text-center py-2 px-3 text-xs font-medium text-gray-600">Min</th>}
                  <th className="text-right py-2 px-3 text-xs font-medium text-gray-600">Charge</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {lineItems.flatMap((item: any, itemIdx: number) => {
                  const cptCodes = item.metadata?.cpt_codes || [];
                  const encId = item.metadata?.encounter_id;
                  const visitType = encId && visitNotes[encId]?.visit_type;
                  const posCode = POS_MAP[visitType] || '11';

                  if (cptCodes.length === 0) {
                    return [(
                      <tr key={itemIdx} className={itemIdx % 2 === 1 ? 'bg-gray-50' : ''}>
                        <td className="py-2 px-3 text-sm">{item.metadata?.service_date ? formatDate(item.metadata.service_date) : ''}</td>
                        <td className="py-2 px-3 text-sm">{posCode}</td>
                        <td className="py-2 px-3 text-sm font-mono">&mdash;</td>
                        <td className="py-2 px-3 text-sm">{item.description}</td>
                        <td className="py-2 px-3 text-sm">&mdash;</td>
                        <td className="py-2 px-3 text-sm text-right">{item.quantity}</td>
                        {showMin && <td className="py-2 px-3 text-sm" />}
                        <td className="py-2 px-3 text-sm text-right font-medium">{formatCurrency(item.total)}</td>
                      </tr>
                    )];
                  }

                  return cptCodes.map((cpt: any, cptIdx: number) => (
                    <tr key={`${itemIdx}-${cptIdx}`} className={(itemIdx + cptIdx) % 2 === 1 ? 'bg-gray-50' : ''}>
                      <td className="py-2 px-3 text-sm">{cptIdx === 0 && item.metadata?.service_date ? formatDate(item.metadata.service_date) : ''}</td>
                      <td className="py-2 px-3 text-sm">{cptIdx === 0 ? posCode : ''}</td>
                      <td className="py-2 px-3 text-sm font-mono">{cpt.code}</td>
                      <td className="py-2 px-3 text-sm">{cpt.description}</td>
                      <td className="py-2 px-3 text-sm">{(cpt.modifiers || []).join(', ') || '—'}</td>
                      <td className="py-2 px-3 text-sm text-right">{cptUnits(cpt)}</td>
                      {showMin && <td className="py-2 px-3 text-sm text-center">{formatCptMinutes(cpt)}</td>}
                      <td className="py-2 px-3 text-sm text-right font-medium">{formatCurrency((cpt.charge || 0) * cptUnits(cpt))}</td>
                    </tr>
                  ));
                })}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="border-t pt-4 mb-6">
            <div className="flex justify-end">
              <div className="w-64 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-medium">{formatCurrency(data.subtotal)}</span>
                </div>
                {(data.tax_amount || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Tax</span>
                    <span className="font-medium">{formatCurrency(data.tax_amount)}</span>
                  </div>
                )}
                {(data.discount_amount || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Discount</span>
                    <span className="font-medium text-green-600">-{formatCurrency(data.discount_amount)}</span>
                  </div>
                )}
                {(data.insurance_portion || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Insurance Portion</span>
                    <span className="font-medium text-blue-600">-{formatCurrency(data.insurance_portion)}</span>
                  </div>
                )}
                {(data.secondary_insurance_portion || 0) > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Secondary Insurance</span>
                    <span className="font-medium text-blue-600">-{formatCurrency(data.secondary_insurance_portion)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t">
                  <span className="font-medium">Balance Due</span>
                  <span className="font-bold text-xl" style={{ color: primaryColor }}>{formatCurrency(data.balance_due)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Visit Notes */}
          {Object.keys(visitNotes).length > 0 && (
            <div className="border-t pt-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Clinical Documentation</h3>
              <div className="space-y-4">
                {Object.entries(visitNotes).map(([encId, note]: [string, any]) => (
                  <div key={encId} className="border rounded-lg overflow-hidden">
                    <div className="px-4 py-3 bg-gray-50 flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-gray-900">
                          {note.date_of_service ? formatDate(note.date_of_service) : 'Visit'}
                        </span>
                        {note.provider?.name && (
                          <span className="text-sm text-gray-500 ml-2">
                            &mdash; {note.provider.name}{note.provider.credentials ? `, ${note.provider.credentials}` : ''}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {note.visit_type && (
                          <span className="text-xs px-2 py-0.5 bg-blue-50 text-blue-700 rounded capitalize">{note.visit_type}</span>
                        )}
                        {note.signed && (
                          <span className="text-xs px-2 py-0.5 bg-green-50 text-green-700 rounded">Signed</span>
                        )}
                      </div>
                    </div>
                    <div className="px-4 py-4 space-y-4 print:block">
                      {note.sections_data?.sections ? (
                        note.sections_data.sections.map((section: any, sIdx: number) => {
                          const fields = section.fields?.filter((f: any) => f.value != null && f.value !== '' && f.value !== false);
                          if (!fields?.length) return null;
                          return (
                            <div key={sIdx}>
                              {section.sectionName && (
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">{section.sectionName}</h4>
                              )}
                              <div className="space-y-1">
                                {fields.map((field: any, fIdx: number) => {
                                  let val = field.value;
                                  if (typeof val === 'object' && val !== null && !Array.isArray(val) && 'content' in val) {
                                    val = val.content;
                                    if (val == null || val === '') return null;
                                  }
                                  let display: React.ReactNode;
                                  if (Array.isArray(val) && val.length > 0 && typeof val[0] === 'object') {
                                    const cols = Object.keys(val[0]);
                                    display = (
                                      <table className="w-full text-sm border-collapse mt-1">
                                        <thead>
                                          <tr>{cols.map(c => <th key={c} className="text-left p-1 border-b text-gray-600 text-xs font-medium">{c}</th>)}</tr>
                                        </thead>
                                        <tbody>
                                          {val.map((row: any, ri: number) => (
                                            <tr key={ri} className="border-b border-gray-100">
                                              {cols.map(c => <td key={c} className="p-1 text-gray-900">{row[c] != null && typeof row[c] === 'object' ? JSON.stringify(row[c]) : String(row[c] ?? '—')}</td>)}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    );
                                  } else if (Array.isArray(val)) {
                                    display = <span className="text-gray-900">{val.map(v => String(v)).join(', ')}</span>;
                                  } else if (typeof val === 'boolean') {
                                    display = <span className="text-gray-900">{val ? 'Yes' : 'No'}</span>;
                                  } else if (typeof val === 'object' && val !== null) {
                                    display = <span className="text-gray-900">{Object.entries(val).filter(([,v]) => v != null).map(([k,v]) => `${k}: ${v}`).join(', ')}</span>;
                                  } else {
                                    display = <span className="text-gray-900">{String(val)}</span>;
                                  }
                                  return (
                                    <div key={fIdx} className="text-sm">
                                      {field.label && !Array.isArray(val) && <span className="text-gray-500 font-medium">{field.label}: </span>}
                                      {field.label && Array.isArray(val) && typeof val[0] === 'object' && <div className="text-gray-500 font-medium mb-1">{field.label}</div>}
                                      {display}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        ['subjective', 'objective', 'assessment', 'plan'].map(key => {
                          let content = note.clinical_note?.[key];
                          if (!content) return null;
                          if (typeof content === 'object' && content?.content) {
                            content = content.content;
                          }
                          if (!content) return null;
                          return (
                            <div key={key}>
                              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{key}</h4>
                              <p className="text-sm text-gray-900 whitespace-pre-wrap leading-relaxed">{content}</p>
                            </div>
                          );
                        })
                      )}

                      {note.addendums?.length > 0 && (
                        <div className="border-t pt-3">
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Addendums</h4>
                          <div className="space-y-2">
                            {note.addendums.map((addendum: any, idx: number) => (
                              <div key={idx} className="bg-yellow-50 border border-yellow-100 rounded p-3">
                                <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
                                  {addendum.created_at && <span>{formatDate(addendum.created_at)}</span>}
                                  {addendum.created_by && <span>by {addendum.created_by}</span>}
                                  {addendum.reason && <span className="italic">({addendum.reason})</span>}
                                </div>
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">{addendum.content}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {note.signed && note.signature_attestation && (
                        <div className="border-t pt-3 text-xs text-gray-500 italic">
                          {note.signature_attestation}
                          {note.signed_at && <span className="ml-1">&mdash; {formatDate(note.signed_at)}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="mt-8 pt-4 border-t text-center text-xs text-gray-400">
            Generated on {formatDate(Date.now())}
          </div>
        </div>
      </div>
    </div>
  );
}
